import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  X, FileText, CheckCircle, Users, Download, Send,
  AlertCircle, Clock, Loader2, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Share2, List, ArrowRight, MapPin, PenLine, Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import SignatureModal from './SignatureModal';
import requests from '../../../services/httpServices';
import * as pdfjsLib from 'pdfjs-dist';
import { useMeetingState } from '../../../context/MeetingStateContext';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export default function ContractRenderer({
  contract,
  onClose,
  onUpdate,
  isHost = false,
  currentUserName = 'User',
  currentUserId = '',
  currentUserEmail = '',
  meetingId = '',
  onSendToParticipants,
}) {
  const meetingState = useMeetingState();
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const pdfContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Track mobile breakpoint
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Field values filled by participant/host
  const [fieldValues, setFieldValues] = useState({});

  // Signature modal state
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureModalField, setSignatureModalField] = useState(null);

  // Host: whether contract has been sent to participants
  const [sentToParticipants, setSentToParticipants] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');

  // Validation errors: { fieldId: errorMessage }
  const [validationErrors, setValidationErrors] = useState({});

  // Host submissions view
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }

  // Field guide sidebar
  const [showFieldGuide, setShowFieldGuide] = useState(false);

  const editableFields = contract.editable_fields || [];
  const isFullscreen = contract.display_mode === 'fullscreen';

  // Fields for the current user role
  const myFields = editableFields.filter(f =>
    isHost ? f.role === 'host' : f.role === 'participant'
  );
  const otherFields = editableFields.filter(f =>
    isHost ? f.role === 'participant' : f.role === 'host'
  );

  // Group fields by page for the field guide
  const fieldsByPage = editableFields.reduce((acc, field) => {
    const p = field.page || 1;
    if (!acc[p]) acc[p] = [];
    acc[p].push(field);
    return acc;
  }, {});
  const pagesWithFields = Object.keys(fieldsByPage).map(Number).sort((a, b) => a - b);
  const myFieldsByPage = myFields.reduce((acc, field) => {
    const p = field.page || 1;
    if (!acc[p]) acc[p] = [];
    acc[p].push(field);
    return acc;
  }, {});

  // Find next page with my unfilled fields
  const getNextUnfilledPage = () => {
    for (const field of myFields) {
      const val = fieldValues[field.id];
      const isFilled = field.type === 'checkbox' ? !!val :
        (field.type === 'signature' || field.type === 'initials') ? (val && val.startsWith('data:image')) :
        (val && String(val).trim());
      if (!isFilled && field.page !== currentPage) return field.page;
    }
    return null;
  };

  // Load document pages (PDF, image, or DOCX that was converted)
  useEffect(() => {
    if (!contract.file_url) return;
    // Strip query params before getting extension
    const cleanUrl = (contract.file_url || '').split('?')[0];
    const ext = (contract.file_name || cleanUrl || '').split('.').pop().toLowerCase();
    const fileType = contract.file_type || (ext === 'pdf' ? 'pdf' : ['jpg', 'jpeg', 'png'].includes(ext) ? 'image' : ext === 'docx' ? 'docx' : '');

    if (fileType === 'pdf') {
      loadPdf(contract.file_url);
    } else if (fileType === 'docx') {
      // DOCX should have been converted to PDF during upload, but if it wasn't,
      // try converting on the fly
      (async () => {
        try {
          const convertResult = await requests.post('/contract/convert-docx', { fileUrl: contract.file_url });
          if (convertResult.success && convertResult.pdfUrl) {
            loadPdf(convertResult.pdfUrl);
          } else {
            toast.error('Failed to load DOCX document');
          }
        } catch (err) {
          console.error('DOCX conversion failed:', err);
          toast.error('Failed to convert DOCX. Please ask host to re-upload as PDF.');
        }
      })();
    } else if (fileType === 'image' || ['jpg', 'jpeg', 'png'].includes(ext)) {
      loadImage(contract.file_url);
    }
  }, [contract.file_url, contract.file_name, contract.file_type]);

  const loadPdf = async (url) => {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      setTotalPages(pdf.numPages);
      setCurrentPage(1);

      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
        pages.push({
          pageNum: i,
          dataUrl: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height,
        });
      }
      setPdfPages(pages);
    } catch (err) {
      console.error('Failed to load PDF:', err);
    }
  };

  const loadImage = (url) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxWidth = 900;
      const maxHeight = 1200;
      let scale = 1;
      if (img.width > maxWidth) scale = Math.min(scale, maxWidth / img.width);
      if (img.height * scale > maxHeight) scale = Math.min(scale, maxHeight / img.height);
      setPdfPages([{
        pageNum: 1,
        dataUrl: url,
        width: img.width * scale,
        height: img.height * scale,
      }]);
      setTotalPages(1);
      setCurrentPage(1);
    };
    img.onerror = () => {
      setPdfPages([{ pageNum: 1, dataUrl: url, width: 900, height: 1200 }]);
      setTotalPages(1);
      setCurrentPage(1);
    };
    img.src = url;
  };

  // Auto-fit PDF scale on mobile to fill the screen width
  useEffect(() => {
    if (!isMobile || pdfPages.length === 0) return;
    const container = pdfContainerRef.current;
    if (!container) return;
    // Wait a frame for layout to settle (full-screen container)
    requestAnimationFrame(() => {
      const containerWidth = container.clientWidth - 16;
      const pageWidth = pdfPages[0]?.width || 900;
      if (containerWidth < pageWidth) {
        setPdfScale(containerWidth / pageWidth);
      }
    });
  }, [pdfPages, isMobile]);

  // Pre-fill host values and auto-fill dates
  useEffect(() => {
    const initial = {};
    editableFields.forEach(field => {
      if (field.value) {
        initial[field.id] = field.value;
      } else if (field.type === 'date' && field.autoFillDate) {
        initial[field.id] = new Date().toISOString().split('T')[0];
      }
    });
    setFieldValues(initial);
  }, [contract]);

  // Check if participant already submitted
  useEffect(() => {
    if (!isHost && meetingId && currentUserId) {
      const contractId = contract._id || contract.id || '';
      requests.get(`/contract/submission/${meetingId}/${currentUserId}?contractElementId=${contractId}`)
        .then(data => {
          if (data.submitted) {
            setSubmitted(true);
            setExistingSubmission(data.submission);
            if (data.submission?.submissionUrl) {
              setDownloadUrl(data.submission.submissionUrl);
            }
            const vals = {};
            data.submission?.filledFields?.forEach(f => {
              vals[f.fieldId] = f.value;
            });
            setFieldValues(vals);
          }
        })
        .catch(() => {});
    }
  }, [isHost, meetingId, currentUserId, contract._id, contract.id]);

  // Load submissions once on mount for host
  const loadSubmissions = useCallback(async () => {
    if (!isHost || !meetingId) return;
    setLoadingSubmissions(true);
    try {
      const contractId = contract._id || contract.id || '';
      const data = await requests.get(`/contract/submissions/${meetingId}?contractElementId=${contractId}`);
      if (data.success) {
        setSubmissions(data.submissions);
      }
    } catch (err) {
      // silent
    } finally {
      setLoadingSubmissions(false);
    }
  }, [isHost, meetingId, contract._id, contract.id]);

  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (isHost && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadSubmissions();
    }
  }, [isHost, loadSubmissions]);

  // Real-time: new submissions arrive via Socket.IO — no polling
  useEffect(() => {
    const socket = meetingState?.socket;
    if (!isHost || !socket) return;
    const contractId = contract._id || contract.id || '';
    const handler = (data) => {
      if (data.contractElementId === contractId && data.submission) {
        setSubmissions(prev => {
          if (prev.some(s => (s._id || s.id) === (data.submission._id || data.submission.id))) return prev;
          // Play chime sound for new submission
          const audio = new Audio('/audio/chime.wav');
          audio.volume = 0.6;
          audio.play().catch(() => {});
          return [data.submission, ...prev];
        });
      }
    };
    socket.on('contract:submission-added', handler);
    return () => socket.off('contract:submission-added', handler);
  }, [isHost, meetingState?.socket, contract._id, contract.id]);

  const updateFieldValue = (fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
    clearFieldError(fieldId);
  };

  // Clear validation error when a field value changes
  const clearFieldError = (fieldId) => {
    setValidationErrors(prev => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const validateFields = () => {
    const errors = {};

    for (const field of myFields) {
      if (!field.required) continue;
      const val = fieldValues[field.id];

      if (field.type === 'signature' || field.type === 'initials') {
        const hasImage = val && val.startsWith('data:image');
        if (!hasImage) {
          errors[field.id] = `${field.label} is required`;
        }
      } else if (field.type === 'checkbox') {
        if (!val) {
          errors[field.id] = `${field.label} must be checked`;
        }
      } else {
        if (!val || !String(val).trim()) {
          errors[field.id] = `${field.label} is required`;
        }
      }
    }

    setValidationErrors(errors);

    const errorFieldIds = Object.keys(errors);
    if (errorFieldIds.length > 0) {
      // Navigate to the page of the first error field
      const firstErrorField = myFields.find(f => errors[f.id]);
      if (firstErrorField && firstErrorField.page !== currentPage) {
        setCurrentPage(firstErrorField.page);
      }

      // Show summary toast
      if (errorFieldIds.length === 1) {
        toast.error(Object.values(errors)[0]);
      } else {
        toast.error(`Please fill in ${errorFieldIds.length} required fields before submitting`);
      }
      return false;
    }
    return true;
  };

  // Validate host fields before sending to participants
  // Checks: 1) All required host-role fields must be filled
  //          2) ALL signature/initials fields (any role) must be signed before sending
  const validateHostFields = () => {
    const errors = {};

    for (const field of editableFields) {
      const isHostField = field.role === 'host';
      const isSigType = field.type === 'signature' || field.type === 'initials';

      // Only validate fields assigned to the host — participants fill theirs after receiving
      if (!isHostField) continue;

      // For non-signature host fields: skip if not required
      if (!field.required && !isSigType) continue;

      if (isSigType) {
        const existingVal = fieldValues[field.id];
        const hasImage = existingVal && existingVal.startsWith('data:image');
        if (!hasImage) {
          const sigLabel = field.type === 'initials' ? 'Initials' : 'Signature';
          errors[field.id] = `${sigLabel} is required — please sign before sending`;
        }
      } else if (field.type === 'checkbox') {
        if (!fieldValues[field.id]) {
          errors[field.id] = `${field.label} must be checked`;
        }
      } else {
        const val = fieldValues[field.id] || field.value || '';
        if (!val || !String(val).trim()) {
          errors[field.id] = `${field.label} is required`;
        }
      }
    }

    setValidationErrors(errors);

    const errorFieldIds = Object.keys(errors);
    if (errorFieldIds.length > 0) {
      const firstErrorField = editableFields.find(f => errors[f.id]);
      if (firstErrorField && firstErrorField.page !== currentPage) {
        setCurrentPage(firstErrorField.page);
      }

      if (errorFieldIds.length === 1) {
        toast.error(Object.values(errors)[0], { duration: 5000 });
      } else {
        toast.error(`Please fill in ${errorFieldIds.length} required fields before sending`, { duration: 5000 });
      }
      return false;
    }
    return true;
  };

  // Host sends contract to participants
  const handleSendToParticipants = () => {
    // Validate all required host fields are filled
    if (!validateHostFields()) return;

    // Build the contract payload with host-filled values baked in
    const updatedFields = editableFields.map(field => {
      if (field.role === 'host') {
        const val = fieldValues[field.id] || field.value || '';
        return { ...field, value: val };
      }
      return field;
    });

    const contractPayload = {
      ...contract,
      editable_fields: updatedFields,
    };

    // Save updated fields back
    if (onUpdate) {
      onUpdate(contractPayload);
    }

    // Send to participants
    if (onSendToParticipants) {
      onSendToParticipants(contractPayload);
    }

    setSentToParticipants(true);
    setShowSubmissions(true);
    toast.success('Contract sent to participants!');
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    // Collect all field values
    const filledFields = editableFields.map(field => {
      let value = fieldValues[field.id] || '';

      if (field.type === 'checkbox') {
        value = fieldValues[field.id] ? 'true' : 'false';
      }

      return {
        fieldId: field.id,
        label: field.label,
        type: field.type,
        role: field.role || 'participant',
        value,
        page: field.page,
        x: field.x,
        y: field.y,
        width: field.width,
        height: field.height,
        fontSize: field.fontSize || 12,
        fontColor: field.fontColor || '#000000',
      };
    });

    setSubmitting(true);
    try {
      const payload = {
        contractElementId: contract._id || contract.id || '',
        meetingId,
        participantId: currentUserId,
        participantName: currentUserName,
        participantEmail: currentUserEmail,
        originalDocumentUrl: contract.file_url || '',
        filledFields,
        hostId: contract.hostId || '',
      };

      const result = await requests.post('/contract/submit', payload);

      if (result.success) {
        setSubmitted(true);
        setExistingSubmission(result.submission);
        if (result.downloadUrl) {
          setDownloadUrl(result.downloadUrl);
        } else if (result.submission?.submissionUrl) {
          setDownloadUrl(result.submission.submissionUrl);
        }
        toast.success('Contract submitted successfully!');
      } else {
        toast.error(result.message || 'Failed to submit contract');
      }
    } catch (error) {
      console.error('Contract submission failed:', error);
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to submit contract. Please try again.';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadOriginal = () => {
    if (contract.file_url) window.open(contract.file_url, '_blank');
  };

  const handleDownloadFilled = () => {
    if (downloadUrl) window.open(downloadUrl, '_blank');
  };

  const getFieldColor = (type) => {
    const colors = { text: '#6a1fbf', signature: '#00c853', date: '#ff9800', initials: '#2196f3', checkbox: '#e91e63' };
    return colors[type] || '#6a1fbf';
  };

  // Render a field overlay on the PDF
  const renderFieldOverlay = (field) => {
    const isMine = isHost ? field.role === 'host' : field.role === 'participant';
    const color = getFieldColor(field.type);
    const isDisabled = submitted;

    const fSize = field.fontSize || 12;
    const fColor = field.fontColor || '#000000';

    // For viewing a specific submission, show its values
    if (viewingSubmission) {
      const filledField = viewingSubmission.filledFields?.find(f => f.fieldId === field.id);
      const val = filledField?.value || '';

      return (
        <div
          key={field.id}
          className="absolute overflow-hidden"
          style={{
            left: `${field.x}%`,
            top: `${field.y}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            zIndex: 10,
          }}
        >
          {val.startsWith('data:image') ? (
            <img src={val} alt={field.label} className="w-full h-full object-contain" />
          ) : field.type === 'checkbox' ? (
            <div className="w-full h-full flex items-center justify-center">
              {val === 'true' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-400" />}
            </div>
          ) : (
            <div className="w-full h-full flex items-center px-1" style={{ fontSize: `${fSize}px`, color: fColor }}>
              {val}
            </div>
          )}
        </div>
      );
    }

    // Show other role fields as read-only labels with their pre-filled values
    if (!isMine) {
      const val = fieldValues[field.id] || field.value || '';
      const isSigField = field.type === 'signature' || field.type === 'initials';

      // Signature/initials for other role — card style
      if (isSigField) {
        const otherRoleLabel = field.role === 'host' ? 'Host' : 'Participant';
        const sigLabel = field.type === 'initials' ? 'Initials' : 'Signature';
        return (
          <div
            key={field.id}
            className="absolute rounded-xl overflow-hidden flex flex-col"
            style={{
              left: `${field.x}%`,
              top: `${field.y}%`,
              width: `${field.width}%`,
              height: `${field.height}%`,
              backgroundColor: '#ffffff',
              border: `2px solid ${color}`,
              zIndex: 10,
            }}
          >
            <div className="flex items-center gap-0.5 px-1.5" style={{ borderBottom: `1px solid ${color}20`, height: '18px', minHeight: '18px' }}>
              <Users className="w-2.5 h-2.5 flex-shrink-0" style={{ color }} />
              <span className="font-medium truncate" style={{ fontSize: '8px', color: '#555' }}>{otherRoleLabel} {sigLabel}</span>
            </div>
            <div className="flex-1 flex items-center justify-center px-1 overflow-hidden">
              {val && val.startsWith('data:image') ? (
                <img src={val} alt={field.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
              ) : (
                <span className="text-gray-500 italic text-center" style={{ fontSize: '9px' }}>
                  Awaiting {otherRoleLabel.toLowerCase()} {sigLabel.toLowerCase()}
                </span>
              )}
            </div>
          </div>
        );
      }

      return (
        <div
          key={field.id}
          className="absolute border border-dashed rounded overflow-hidden flex items-center justify-center"
          style={{
            left: `${field.x}%`,
            top: `${field.y}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            borderColor: color + '60',
            backgroundColor: color + '10',
            zIndex: 10,
          }}
        >
          {val ? (
            <span className="px-1" style={{ fontSize: `${fSize}px`, color: fColor }}>{val}</span>
          ) : (
            <span className="text-gray-400 px-1 italic" style={{ fontSize: `${Math.max(8, fSize - 2)}px` }}>{field.label}</span>
          )}
        </div>
      );
    }

    // Render input for current user's fields
    const hasError = !!validationErrors[field.id];
    const errorBorderColor = '#ef4444';

    if (field.type === 'signature' || field.type === 'initials') {
      const existingVal = fieldValues[field.id];
      const hasExistingImage = existingVal && existingVal.startsWith('data:image');
      const myRoleLabel = isHost ? 'Host' : 'Participant';
      const sigLabel = field.type === 'initials' ? 'Initials' : 'Signature';

      return (
        <div
          key={field.id}
          className="absolute rounded-xl overflow-hidden cursor-pointer group"
          style={{
            left: `${field.x}%`,
            top: `${field.y}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            backgroundColor: '#ffffff',
            border: `2px solid ${hasError ? errorBorderColor : color}`,
            zIndex: 10,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled) {
              clearFieldError(field.id);
              setSignatureModalField(field);
              setSignatureModalOpen(true);
            }
          }}
        >
          {/* Compact header label */}
          <div
            className="flex items-center gap-0.5 px-1.5"
            style={{ borderBottom: `1px solid ${hasError ? errorBorderColor : color}20`, height: '18px', minHeight: '18px' }}
          >
            <Users className="w-2.5 h-2.5 flex-shrink-0" style={{ color: hasError ? errorBorderColor : color }} />
            <span
              className="font-medium truncate"
              style={{ fontSize: '8px', color: '#555' }}
            >
              {myRoleLabel} {sigLabel}
            </span>
            {hasExistingImage && !isDisabled && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  updateFieldValue(field.id, '');
                }}
                className="ml-auto p-0.5 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </div>

          {/* Content area */}
          <div className="flex-1 flex items-center justify-center px-1 overflow-hidden" style={{ height: 'calc(100% - 18px)' }}>
            {hasExistingImage ? (
              <img src={existingVal} alt={field.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
            ) : (
              <button
                className="w-full py-1.5 rounded-lg text-white font-medium transition-all hover:opacity-90 flex items-center justify-center gap-1"
                style={{
                  backgroundColor: hasError ? errorBorderColor : '#7c3aed',
                  fontSize: '11px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    clearFieldError(field.id);
                    setSignatureModalField(field);
                    setSignatureModalOpen(true);
                  }
                }}
              >
                <PenLine className="w-3 h-3" />
                Sign Here
              </button>
            )}
          </div>
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <div
          key={field.id}
          className="absolute rounded overflow-hidden flex flex-col items-center justify-center cursor-pointer"
          style={{
            left: `${field.x}%`,
            top: `${field.y}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            border: `2px solid ${hasError ? errorBorderColor : color}`,
            backgroundColor: hasError ? 'rgba(254,226,226,0.9)' : fieldValues[field.id] ? color + '30' : 'rgba(255,255,255,0.9)',
            zIndex: 10,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled) updateFieldValue(field.id, !fieldValues[field.id]);
          }}
        >
          {fieldValues[field.id] && <CheckCircle className="w-4 h-4" style={{ color }} />}
          {hasError && (
            <span className="absolute -bottom-5 left-0 right-0 text-center text-red-500 whitespace-nowrap" style={{ fontSize: '9px' }}>
              Required
            </span>
          )}
        </div>
      );
    }

    const fieldFontSize = field.fontSize || 12;
    const fieldFontColor = field.fontColor || '#000000';

    if (field.type === 'date') {
      const isAutoFilled = field.autoFillDate;
      return (
        <div
          key={field.id}
          className="absolute rounded overflow-hidden"
          style={{
            left: `${field.x}%`,
            top: `${field.y}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {isAutoFilled && !isHost ? (
            <div
              className="w-full h-full px-1 bg-gray-50 border-2 rounded flex items-center"
              style={{ fontSize: `${fieldFontSize}px`, color: fieldFontColor, borderColor: hasError ? errorBorderColor : color }}
            >
              {fieldValues[field.id] || ''}
            </div>
          ) : (
            <input
              type="date"
              value={fieldValues[field.id] || ''}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              disabled={isDisabled}
              className={`w-full h-full px-1 border-2 rounded ${hasError ? 'bg-red-50' : 'bg-white'}`}
              style={{ fontSize: `${fieldFontSize}px`, color: fieldFontColor, borderColor: hasError ? errorBorderColor : color }}
            />
          )}
          {hasError && (
            <span className="absolute -bottom-4 left-0 text-red-500 whitespace-nowrap" style={{ fontSize: '9px' }}>
              {validationErrors[field.id]}
            </span>
          )}
        </div>
      );
    }

    // Text / initials input
    return (
      <div
        key={field.id}
        className="absolute rounded overflow-hidden"
        style={{
          left: `${field.x}%`,
          top: `${field.y}%`,
          width: `${field.width}%`,
          height: `${field.height}%`,
          zIndex: 10,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={fieldValues[field.id] || ''}
          onChange={(e) => updateFieldValue(field.id, e.target.value)}
          disabled={isDisabled}
          placeholder={field.placeholder || field.label}
          className={`w-full h-full px-1 border-2 rounded ${hasError ? 'bg-red-50' : 'bg-white'}`}
          style={{ fontSize: `${fieldFontSize}px`, color: fieldFontColor, borderColor: hasError ? errorBorderColor : color }}
        />
        {hasError && (
          <span className="absolute -bottom-4 left-0 text-red-500 whitespace-nowrap" style={{ fontSize: '9px' }}>
            {validationErrors[field.id]}
          </span>
        )}
      </div>
    );
  };

  const currentPageData = pdfPages.find(p => p.pageNum === currentPage);
  const currentPageFields = editableFields.filter(f => f.page === currentPage);
  const isPdfView = pdfPages.length > 0;

  const handleDeleteSubmission = async (submissionId) => {
    try {
      const result = await requests.delete(`/contract/submission/${submissionId}`);
      if (result.success) {
        setSubmissions(prev => prev.filter(s => (s._id || s.id) !== submissionId));
        if (viewingSubmission && (viewingSubmission._id || viewingSubmission.id) === submissionId) {
          setViewingSubmission(null);
        }
        toast.success('Submission deleted');
      } else {
        toast.error(result.message || 'Failed to delete submission');
      }
    } catch (err) {
      toast.error('Failed to delete submission');
    }
  };

  // Render submission detail for host
  const renderSubmissionCard = (sub) => (
    <div
      key={sub._id || sub.id}
      className={`bg-white/5 border rounded-lg p-3 cursor-pointer transition-colors ${
        viewingSubmission?._id === sub._id ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 hover:bg-white/10'
      }`}
      onClick={() => setViewingSubmission(viewingSubmission?._id === sub._id ? null : sub)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">{sub.participantName}</p>
          {sub.participantEmail && <p className="text-xs text-gray-400">{sub.participantEmail}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm({ id: sub._id || sub.id, name: sub.participantName });
            }}
            className="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
            title="Delete submission"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        <Clock className="w-3 h-3 inline mr-1" />
        {new Date(sub.createdAt).toLocaleString()}
      </p>
      {sub.ipAddress && (
        <p className="text-xs text-gray-500 mt-0.5 break-all">
          <span className="text-gray-600">IP:</span> {sub.ipAddress}
        </p>
      )}
      {sub.geoLocation && (sub.geoLocation.city || sub.geoLocation.country) && (
        <p className="text-xs text-gray-500 mt-0.5">
          <MapPin className="w-3 h-3 inline mr-1 text-blue-400" />
          {[sub.geoLocation.city, sub.geoLocation.region, sub.geoLocation.country].filter(Boolean).join(', ')}
          {sub.geoLocation.latitude && sub.geoLocation.longitude && (
            <span className="text-gray-600 ml-1">
              ({sub.geoLocation.latitude.toFixed(4)}, {sub.geoLocation.longitude.toFixed(4)})
            </span>
          )}
        </p>
      )}
      {(() => {
        const pdfUrl = sub.submissionUrl && sub.submissionUrl.split('?')[0].endsWith('.pdf') ? sub.submissionUrl : null;
        return pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-blue-400 hover:underline mt-1 inline-flex items-center gap-1"
          >
            <Download className="w-3 h-3" /> Download filled PDF
          </a>
        ) : null;
      })()}
    </div>
  );

  // Participant: post-submission success screen with download
  if (!isHost && submitted) {
    return (
      <div className="absolute top-0 left-0 right-0 bottom-16 md:bottom-20 z-[50] flex items-center justify-center bg-black/40">
        <div className="w-full max-w-lg mx-4 bg-[#0A0F1E] rounded-xl border border-white/10 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">{contract.name}</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Success content */}
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Contract Submitted</h3>
            <p className="text-sm text-gray-400 mb-6">
              Your signed contract has been submitted successfully. You can download your copy below.
            </p>

            <div className="flex flex-col gap-3 w-full max-w-xs">
              {downloadUrl && (
                <button
                  onClick={handleDownloadFilled}
                  className="flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg font-medium hover:opacity-90 transition-all"
                >
                  <Download className="w-5 h-5" />
                  Download My Signed Document
                </button>
              )}
              <button
                onClick={handleDownloadOriginal}
                className="flex items-center justify-center gap-2 px-5 py-2.5 border border-white/10 text-gray-300 rounded-lg text-sm hover:bg-white/10 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Original Document
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 text-sm hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 sm:absolute sm:top-0 sm:left-0 sm:right-0 sm:bottom-16 md:bottom-20 z-[50] flex items-stretch sm:items-center justify-center sm:bg-black/40">
      <div className={`${isFullscreen ? 'w-full h-full' : 'w-full h-full sm:h-auto sm:max-w-5xl sm:max-h-full sm:mx-4'} bg-[#0A0F1E] ${isFullscreen ? '' : 'sm:rounded-xl'} border border-white/10 shadow-2xl flex flex-col`}>
        {/* Header */}
        <div className="px-2 sm:px-3 py-1.5 sm:py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0 gap-1 sm:gap-2">
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-xs sm:text-lg font-semibold text-white truncate">{contract.name}</h2>
              <p className="text-[10px] sm:text-xs text-white hidden sm:block">
                {isHost
                  ? (sentToParticipants
                    ? 'Sent to participants — waiting for submissions'
                    : 'Fill in host fields, then send to participants')
                  : `Fill in the highlighted fields and submit`
                }
                {myFields.length > 0 && ` (${myFields.length} fields for you)`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {isHost && sentToParticipants && (
              <div className="flex items-center gap-1 px-1.5 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-green-400" />
                <span className="text-[10px] sm:text-sm text-green-400">Sent</span>
              </div>
            )}
            <button onClick={handleDownloadOriginal} className="flex items-center gap-1 p-1.5 sm:px-3 sm:py-1.5 border border-white/10 text-gray-300 rounded-lg text-xs sm:text-sm hover:bg-white/10">
              <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              <span className="hidden sm:inline">Original</span>
            </button>
            {isHost && (
              <button
                onClick={() => { setShowSubmissions(!showSubmissions); setViewingSubmission(null); }}
                className={`flex items-center gap-1 p-1.5 sm:px-3 sm:py-1.5 border rounded-lg text-xs sm:text-sm ${
                  showSubmissions ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                <Users className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                <span className="hidden sm:inline">Submissions</span> {submissions.length > 0 && <span className="text-[10px] sm:text-xs">({submissions.length})</span>}
              </button>
            )}
            <button onClick={onClose} className="p-1 sm:p-2 hover:bg-white/10 rounded-lg">
              <X className="w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Host submissions sidebar */}
          {isHost && showSubmissions && (
            <div className="w-full sm:w-64 absolute sm:relative inset-0 sm:inset-auto z-10 bg-[#0A0F1E] sm:bg-transparent border-r border-white/10 overflow-y-auto p-3 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Submissions</p>
                <button onClick={loadSubmissions} className="text-xs text-purple-400 hover:text-purple-300">Refresh</button>
              </div>
              {loadingSubmissions ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No submissions yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {submissions.map(sub => renderSubmissionCard(sub))}
                </div>
              )}
              {viewingSubmission && (
                <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-xs text-yellow-300">
                    Viewing: {viewingSubmission.participantName}'s submission
                  </p>
                  <button
                    onClick={() => setViewingSubmission(null)}
                    className="text-xs text-yellow-400 hover:text-yellow-300 mt-1"
                  >
                    Clear view
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Field Guide sidebar */}
          {showFieldGuide && (
            <div className="w-full sm:w-72 absolute sm:relative inset-0 sm:inset-auto z-10 bg-[#0A0F1E] sm:bg-transparent border-r border-white/10 overflow-y-auto flex-shrink-0">
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-purple-400" />
                    <p className="text-sm font-medium text-white">Field Guide</p>
                  </div>
                  <button onClick={() => setShowFieldGuide(false)} className="p-1 hover:bg-white/10 rounded sm:hidden">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                {/* Summary */}
                <div className="bg-white/5 rounded-lg p-2.5 mb-3 space-y-1">
                  <p className="text-xs text-gray-300">
                    <span className="text-white font-medium">{myFields.length}</span> field(s) for you to fill
                  </p>
                  <p className="text-xs text-gray-400">
                    <span className="text-gray-300">{otherFields.length}</span> pre-filled by {isHost ? 'participants' : 'host'}
                  </p>
                  {totalPages > 1 && (
                    <p className="text-xs text-gray-400">
                      Across <span className="text-gray-300">{pagesWithFields.length}</span> of {totalPages} pages
                    </p>
                  )}
                </div>

                {/* Fields grouped by page */}
                {pagesWithFields.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No fields on this document</p>
                ) : (
                  <div className="space-y-2">
                    {pagesWithFields.map(pageNum => {
                      const pageFields = fieldsByPage[pageNum] || [];
                      const isCurrent = pageNum === currentPage;
                      return (
                        <div key={pageNum} className={`rounded-lg border transition-colors ${isCurrent ? 'border-purple-500/40 bg-purple-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
                          <button
                            onClick={() => setCurrentPage(pageNum)}
                            className="w-full flex items-center justify-between px-3 py-2 text-left"
                          >
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium ${isCurrent ? 'text-purple-300' : 'text-gray-400'}`}>
                                Page {pageNum}
                              </span>
                              {isCurrent && <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/30 text-purple-300 rounded-full">Current</span>}
                            </div>
                            <span className="text-[10px] text-white/70">{pageFields.length} field(s)</span>
                          </button>
                          <div className="px-3 pb-2 space-y-1">
                            {pageFields.map(field => {
                              const isMine = isHost ? field.role === 'host' : field.role === 'participant';
                              const val = fieldValues[field.id];
                              const isFilled = field.type === 'checkbox' ? !!val :
                                (field.type === 'signature' || field.type === 'initials') ? (val && val.startsWith('data:image')) :
                                (val && String(val).trim());
                              const color = getFieldColor(field.type);
                              const hasError = !!validationErrors[field.id];

                              return (
                                <div
                                  key={field.id}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${
                                    hasError ? 'bg-red-500/10 border border-red-500/20' : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                  <div className="flex-1 min-w-0">
                                    <span className={`truncate block ${isMine ? 'text-white' : 'text-gray-500'}`}>
                                      {field.label}
                                    </span>
                                    <span className="text-[10px] text-gray-600">
                                      {field.type} · {field.role === 'host' ? 'Host' : 'Participant'}
                                      {field.required && ' · Required'}
                                    </span>
                                  </div>
                                  {isMine ? (
                                    isFilled ? (
                                      <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                    ) : hasError ? (
                                      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3.5 h-3.5 rounded-full border border-gray-600 flex-shrink-0" />
                                    )
                                  ) : (
                                    <span className="text-[9px] text-gray-600 flex-shrink-0">read-only</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PDF viewer with field overlays */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#111827]">
            {/* Page nav with field indicators */}
            {isPdfView && (
              <div className="border-b border-white/10 bg-[#0A0F1E] flex-shrink-0">
                <div className="flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 gap-1">
                  {/* Left: field guide toggle */}
                  <button
                    onClick={() => setShowFieldGuide(!showFieldGuide)}
                    className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded text-xs transition-colors flex-shrink-0 ${
                      showFieldGuide ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <List className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Fields</span>
                    {myFields.length > 0 && (
                      <span className="px-1 sm:px-1.5 py-0.5 rounded-full bg-purple-500/30 text-purple-300 text-[10px] font-medium">
                        {myFields.length}
                      </span>
                    )}
                  </button>

                  {/* Center: page navigation */}
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="p-0.5 sm:p-1 text-gray-400 hover:text-white disabled:opacity-30">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-[10px] sm:text-xs text-white whitespace-nowrap">{currentPage}/{totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="p-0.5 sm:p-1 text-gray-400 hover:text-white disabled:opacity-30">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Right: zoom + jump to next */}
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      <button onClick={() => setPdfScale(s => Math.max(0.25, s - 0.1))} className="p-0.5 sm:p-1 text-gray-400 hover:text-white"><ZoomOut className="w-3 h-3 sm:w-3.5 sm:h-3.5" /></button>
                      <span className="text-[10px] sm:text-xs text-white w-7 sm:w-10 text-center">{Math.round(pdfScale * 100)}%</span>
                      <button onClick={() => setPdfScale(s => Math.min(2, s + 0.1))} className="p-0.5 sm:p-1 text-gray-400 hover:text-white"><ZoomIn className="w-3 h-3 sm:w-3.5 sm:h-3.5" /></button>
                    </div>
                    {(() => {
                      const nextPage = getNextUnfilledPage();
                      return nextPage ? (
                        <button
                          onClick={() => setCurrentPage(nextPage)}
                          className="flex items-center gap-1 px-1.5 sm:px-2 py-1 bg-orange-500/20 text-orange-300 rounded text-[10px] sm:text-xs hover:bg-orange-500/30 transition-colors"
                          title={`Jump to page ${nextPage}`}
                        >
                          <ArrowRight className="w-3 h-3" />
                          <span className="hidden sm:inline">Next field</span>
                        </button>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Page dots - show which pages have fields (hidden on mobile to save space) */}
                {totalPages > 1 && pagesWithFields.length > 0 && (
                  <div className="hidden sm:flex items-center gap-1 px-3 pb-2 overflow-x-auto">
                    <span className="text-[10px] text-white mr-1 flex-shrink-0">Pages with fields:</span>
                    {pagesWithFields.map(pageNum => {
                      const myCount = (myFieldsByPage[pageNum] || []).length;
                      const totalCount = (fieldsByPage[pageNum] || []).length;
                      const isCurrent = pageNum === currentPage;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all flex-shrink-0 ${
                            isCurrent
                              ? 'bg-purple-500 text-white'
                              : myCount > 0
                                ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                                : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                          title={`Page ${pageNum}: ${myCount} field(s) for you, ${totalCount} total`}
                        >
                          <span>p{pageNum}</span>
                          {myCount > 0 && (
                            <span className={`px-1 rounded-full ${isCurrent ? 'bg-white/30' : 'bg-purple-500/40'}`}>
                              {myCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* PDF page */}
            <div ref={pdfContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden flex items-start justify-center p-2 sm:p-4">
              {isPdfView && currentPageData ? (
                <div
                  className="relative shadow-2xl"
                  style={{
                    width: currentPageData.width * pdfScale,
                    height: currentPageData.height * pdfScale,
                  }}
                >
                  <img
                    src={currentPageData.dataUrl}
                    alt={`Page ${currentPage}`}
                    className="w-full h-full pointer-events-none select-none"
                    draggable={false}
                  />
                  {/* Field overlays */}
                  {currentPageFields.map(field => renderFieldOverlay(field))}
                </div>
              ) : contract.file_url ? (
                <div className="w-full h-full flex flex-col items-center">
                  {contract.file_type === 'image' ? (
                    <img src={contract.file_url} alt={contract.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <iframe src={contract.file_url} title={contract.name} className="w-full h-full border-0 rounded-lg" />
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No document uploaded</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Validation error banner */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="px-3 py-2 bg-red-500/10 border-t border-red-500/20 flex items-center gap-2 flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-xs text-red-400">
              {Object.keys(validationErrors).length === 1
                ? Object.values(validationErrors)[0]
                : `${Object.keys(validationErrors).length} required fields need to be filled:`}
              {Object.keys(validationErrors).length > 1 && (
                <span className="ml-1 text-red-300">
                  {Object.values(validationErrors).map(e => e.replace(' is required', '').replace(' must be checked', '')).join(', ')}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-2 sm:p-3 py-1.5 sm:py-3 border-t border-white/10 flex items-center justify-between flex-shrink-0 gap-1 sm:gap-2">
          <p className="text-[10px] sm:text-xs text-white hidden sm:block">
            {editableFields.length > 0
              ? `${myFields.length} field(s) for you on ${Object.keys(myFieldsByPage).length} page(s) | ${otherFields.length} pre-filled`
              : 'View-only document'
            }
            {currentPageFields.length > 0 && ` · This page: ${currentPageFields.filter(f => isHost ? f.role === 'host' : f.role === 'participant').length} for you`}
          </p>
          <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto justify-end">
            {/* Host: Send to Participants button */}
            {isHost && !sentToParticipants && (
              <button
                onClick={handleSendToParticipants}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg font-medium text-xs sm:text-sm hover:opacity-90 transition-all"
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="sm:hidden">Send</span>
                <span className="hidden sm:inline">Send to Participants</span>
              </button>
            )}
            {/* Participant: Submit button */}
            {!isHost && myFields.length > 0 && !submitted && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg font-medium text-xs sm:text-sm hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Submit & Sign</>
                )}
              </button>
            )}
            <button onClick={onClose} className="px-3 sm:px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 text-xs sm:text-sm">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-[#1a1a2e] rounded-2xl w-full max-w-sm shadow-2xl border border-white/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-white font-semibold text-center text-lg mb-2">Delete Submission</h3>
              <p className="text-gray-400 text-sm text-center">
                Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm.name}</span>'s submission? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-5 sm:px-6 pb-5 sm:pb-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 text-sm text-gray-300 border border-white/10 rounded-xl hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteSubmission(deleteConfirm.id);
                  setDeleteConfirm(null);
                }}
                className="flex-1 px-4 py-2.5 text-sm text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={signatureModalOpen}
        onClose={() => { setSignatureModalOpen(false); setSignatureModalField(null); }}
        onConfirm={(dataUrl) => {
          if (signatureModalField) {
            updateFieldValue(signatureModalField.id, dataUrl);
          }
        }}
        fieldLabel={signatureModalField?.label || ''}
        fieldType={signatureModalField?.type || 'signature'}
        currentValue={signatureModalField ? fieldValues[signatureModalField.id] : null}
        defaultName={currentUserName}
      />
    </div>
  );
}
